import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'

// rsuite
import Table from 'rsuite/Table'
import Tag from 'rsuite/Tag'
import Toggle from 'rsuite/Toggle'
import Tooltip from 'rsuite/Tooltip'
import Whisper from 'rsuite/Whisper'

// includes
import TrashIcon from '@rsuite/icons/Trash'
import {isEmpty} from 'lodash'
import {toast} from 'react-toastify'
import {SortType} from 'rsuite-table/lib/@types/common'
import Pagination from 'rsuite/Pagination'
import IconButton from 'rsuite/esm/IconButton'
import {StateParamsModel} from 'src/app/modules/common/Model'
import CheckPermissions from 'src/app/modules/common/checkPermission'
import DeleteModal from 'src/app/modules/common/components/deleteModal'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import * as nabilAuctionSubscriberRedux from '../index'

const NabilAuctionSubscriberComponent = () => {
  const dispatch = useDispatch()
  const {
    data,
    loading,
    singleEnableSuccess,
    singleDisableSuccess,
    enableSuccess,
    disableSuccess,
    toggleLoading,
    deleteSuccess,
  } = useSelector((state: any) => state.nabilAuctionSubscriber)
  const [params, setParams] = useState<StateParamsModel>({
    page: 1,
    limit: 10,
  })
  const [checkedValues, setCheckedValues] = useState<Array<string>>([])

  // alert modal
  const [alertOpen, setAlertOpen] = useState(false)
  const handleAlertOpen = () => setAlertOpen(true)
  const handleAlertClose = () => setAlertOpen(false)

  const handleChangeLimit = (dataKey: number) => {
    setParams({
      ...params,
      limit: dataKey,
    })
  }

  const enableNabilAuctionSubscriberData = () => {
    if (!isEmpty(checkedValues)) {
      const formData = [...checkedValues]
      dispatch(nabilAuctionSubscriberRedux.actions.enableNabilAuctionSubscriber(formData))
    } else {
      toast.error('No data Selected')
    }
  }

  const disableNabilAuctionSubscriberData = () => {
    if (!isEmpty(checkedValues)) {
      const formData = [...checkedValues]
      dispatch(nabilAuctionSubscriberRedux.actions.disableNabilAuctionSubscriber(formData))
    } else {
      toast.error('No data Selected')
    }
  }

  const ActionCell = ({rowData, dataKey, ...props}: any) => {
    return (
      <Cell {...props} className='link-group' data-cy='nabil-auction-subscriber-link-group'>
        <CheckPermissions type='Delete'>
          <Whisper
            placement='top'
            trigger='hover'
            data-cy='nabil-auction-subscriber-delete-button'
            speaker={<Tooltip>Delete</Tooltip>}
          >
            <IconButton
              appearance='subtle'
              onClick={() => {
                handleAlertOpen()
                let nabilAuctionSubscriberId = [rowData.id]
                setCheckedValues(nabilAuctionSubscriberId)
              }}
              icon={<TrashIcon />}
              data-cy='delete-button'
            />
          </Whisper>
        </CheckPermissions>
        <Whisper
          placement='top'
          trigger='hover'
          data-cy='nabil-auction-subscriber-status-button'
          speaker={<Tooltip>Status</Tooltip>}
        >
          <Toggle
            size='sm'
            disabled={toggleLoading}
            checked={rowData.status}
            onClick={() => handleToggleAction(rowData)}
            data-cy='toggle-button'
          />
        </Whisper>
      </Cell>
    )
  }

  const handleToggleAction = (data: {[key: string]: string}) => {
    data?.status
      ? dispatch(nabilAuctionSubscriberRedux.actions.singleDisableNabilAuctionSubscriber(data.id))
      : dispatch(nabilAuctionSubscriberRedux.actions.singleEnableNabilAuctionSubscriber(data.id))
  }

  const Cell = Table.Cell

  const columns = [
    {
      label: 'S.N.',
      dataKey: 'sn',
      width: 60,
      cell: <Cell dataKey='sn' />,
    },
    {
      label: 'Name',
      dataKey: 'name',
      flexGrow: 1,
      cell: <Cell dataKey='name' />,
      sortable: true,
    },
    {
      label: 'Email',
      dataKey: 'email',
      flexGrow: 1,
      cell: <Cell dataKey='email' />,
      sortable: true,
    },
    {
      label: 'Mobile Number',
      dataKey: 'mobile_number',
      width: 150,
      cell: <Cell dataKey='mobile_number' />,
      sortable: true,
    },
    {
      label: 'Status',
      dataKey: 'status',
      width: 85,
      cell: (
        <Cell>
          {(rowData) =>
            rowData?.status ? (
              <Tag color='green' size='sm'>
                Active
              </Tag>
            ) : (
              <Tag color='red' size='sm'>
                Inactive
              </Tag>
            )
          }
        </Cell>
      ),
      sortable: false,
    },
    {
      label: 'Action',
      width: 150,
      align: 'center',
      cell: <ActionCell dataKey='id' />,
    },
  ]

  useEffect(() => {
    dispatch(nabilAuctionSubscriberRedux.actions.getNabilAuctionSubscriber(params))
  }, [params])

  const handleChecked = (values: Array<string>) => {
    setCheckedValues(values)
  }

  useEffect(() => {
    if (deleteSuccess) {
      toast.success('Nabil Auction Subscriber deleted successfully')
    }
    if (enableSuccess) {
      toast.success('Nabil Auction Subscriber enabled successfully')
    }
    if (disableSuccess) {
      toast.success('Nabil Auction Subscriber disabled successfully')
    }
    handleChecked([])
    dispatch(nabilAuctionSubscriberRedux?.actions.getNabilAuctionSubscriber(params))
  }, [deleteSuccess, enableSuccess, disableSuccess])

  useEffect(() => {
    if (singleEnableSuccess) {
      toast.success('Nabil Auction Subscriber enabled successfully')
    }
    if (singleDisableSuccess) {
      toast.success('Nabil Auction Subscriber disabled successfully')
    }
    handleChecked([])
  }, [singleDisableSuccess, singleEnableSuccess])

  const nabilAuctionSubscriberData = data?.nabilAuctionSubscriber
    ? data?.nabilAuctionSubscriber?.map((item: any, i: number) => ({
        sn: (params?.page - 1) * params?.limit + (i + 1),
        id: item.id,
        name: item.name,
        email: item.email,
        mobile_number: item.mobile_number,
        status: item.status,
      }))
    : []

  const handleSort = (dataKey: string, sortType?: SortType) => {
    setParams({...params, orderBy: dataKey, order: sortType})
  }

  const handleRefresh = () => {
    dispatch(nabilAuctionSubscriberRedux.actions.getNabilAuctionSubscriber(params))
  }

  const handleGetAllData = () => {
    setParams({...params, limit: data?.meta?.total})
    dispatch(nabilAuctionSubscriberRedux.actions.getNabilAuctionSubscriber(params))
  }

  const handleDelete = () => {
    dispatch(nabilAuctionSubscriberRedux?.actions?.deleteNabilAuctionSubscriber([...checkedValues]))
    handleAlertClose()
  }

  return (
    <>
      <div className='shadow p-3 bg-white rounded' data-cy='nabil-auction-subscriber-shadow-comp'>
        <DesignComponent
          moduleName='Nabil Auction Subscribers'
          params={params}
          setParams={setParams}
          handleRefresh={handleRefresh}
          handleAlertOpen={handleAlertOpen}
          exportShow={false}
          importShow={false}
          enableMultiple={enableNabilAuctionSubscriberData}
          disableMultiple={disableNabilAuctionSubscriberData}
          data={nabilAuctionSubscriberData}
          addShow={false}
          handleGetAllData={handleGetAllData}
          loading={loading}
          data-cy='nabil-auction-subscriber-design-comp'
        />

        <div className='datatable' data-cy='nabil-auction-subscriber-datatable'>
          <RSuiteTable
            onChecked={handleChecked}
            showCheckbox={true}
            columns={columns}
            data={nabilAuctionSubscriberData}
            checkedValues={checkedValues}
            showLoading={loading}
            handleSort={handleSort}
            data-cy='nabil-auction-subscriber-r-suite'
          />
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            className='mt-5'
            maxButtons={5}
            size='sm'
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={data?.meta?.total || 0}
            limitOptions={[10, 20, 50, 100]}
            limit={params?.limit}
            activePage={params?.page}
            onChangePage={(value) => setParams({...params, page: value})}
            onChangeLimit={handleChangeLimit}
            data-cy='nabil-auction-subscriber-pagination'
          />
        </div>

        {/* Delete Modal */}
        <>
          {alertOpen &&
            (!isEmpty(checkedValues) ? (
              <DeleteModal
                handleClick={() => handleDelete()}
                isOpen={alertOpen}
                handleClose={() => handleAlertClose()}
              />
            ) : (
              toast.error('No data selected') && setAlertOpen(false)
            ))}
        </>
      </div>
    </>
  )
}

export default NabilAuctionSubscriberComponent
